/**
 * The global half of the fingerprint coverage: everything that has to happen
 * once per page rather than once per call site.
 *
 * Called from `packs/header_components.js`, which is the one entry every page
 * reaches through `_shared/_header_scripts.haml` (both its `non_vue_page` and
 * its `append_javascript_pack_tag` branch). Priming from a per-page include is
 * precisely why `/login` was uncovered in the first place, so this rides the
 * globally-emitted pack instead of adding a new one.
 *
 * It lives here rather than inline in the pack so it can be unit-tested without
 * dragging in the pack's Vue/SitewideSearch import chain — the jQuery-instance
 * assertion below is the single highest-risk detail in this change, and a test
 * that cannot run is not a guard.
 *
 * Three registrations, all silent on failure:
 *
 *  1. the default `axios` instance — closes the ~30 files that import `axios`
 *     directly instead of going through `AxiosService`;
 *  2. a `$.ajaxPrefilter` on **`window.jQuery`** — see the instance trap below;
 *  3. the hidden `user[fingerprint]` field on the three login views.
 *
 * ## The jQuery instance trap
 *
 * `config/webpack/base.js` installs a `ProvidePlugin` mapping `$` / `jQuery` /
 * `jquery` to the npm `jquery` **bundled into the pack**. The 58 sprockets files
 * under `app/assets/javascripts/` call the **sprockets** jQuery, which lives on
 * `window.jQuery`. A prefilter registered on the pack-local `$` therefore
 * intercepts none of them — with no error, and no failing test unless the test
 * asserts *which* instance was registered on. So this registers on
 * `window.jQuery` explicitly, and additionally on the pack-local instance when
 * it is a different object (cheap, and it covers pack-side `$.ajax`).
 *
 * `layouts/application.haml` loads the sprockets bundle with
 * `async: Rails.env.production?`, so `window.jQuery` may not exist yet when this
 * pack evaluates. Registration is attempted on DOMContentLoaded, retried once on
 * `window.load`, and then abandoned silently — a missing hook must never throw.
 */
import axios from 'axios'
import {
  FINGERPRINT_HEADER,
  fingerprintHeadersFor,
  primeFingerprint,
} from './fingerprintHolder'

export const LOGIN_FIELD_SELECTOR = 'input[name="user[fingerprint]"]'

// Marks an object we have already hooked, so a double-install (the pack
// evaluating twice, a retry firing after a successful first attempt) cannot
// register two prefilters or two interceptors.
const INSTALLED_FLAG = '__spectoraFingerprintAttached'

/**
 * Attach the header to every request the *default* axios instance issues.
 *
 * `AxiosService` has its own interceptor and is untouched by this. Idempotent
 * per instance.
 *
 * @param {Object} [instance] - Axios instance; defaults to the global one.
 * @returns {boolean} True when an interceptor is registered (now or already).
 */
export const installAxiosDefaultInterceptor = (instance = axios) => {
  if (!instance || !instance.interceptors) return false
  if (instance[INSTALLED_FLAG]) return true

  instance[INSTALLED_FLAG] = true
  instance.interceptors.request.use(
    (config) => {
      try {
        Object.assign(
          config.headers,
          fingerprintHeadersFor(config.url, config.baseURL)
        )
      } catch (error) {
        // Telemetry is never worth failing a request over.
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  return true
}

/**
 * Register the `X-Fingerprint` prefilter on one jQuery instance.
 *
 * Reads the primed value **synchronously** — `ajaxPrefilter` cannot await — so a
 * cold browser's first request simply goes out without the header rather than
 * waiting for one.
 *
 * @param {Object} jq - A jQuery instance.
 * @returns {boolean} True when the prefilter is registered (now or already).
 */
export const registerJqueryPrefilter = (jq) => {
  if (!jq || typeof jq.ajaxPrefilter !== 'function') return false
  if (jq[INSTALLED_FLAG]) return true

  jq.ajaxPrefilter((options, originalOptions, jqXHR) => {
    try {
      const value = fingerprintHeadersFor(options && options.url)[
        FINGERPRINT_HEADER
      ]
      if (value) jqXHR.setRequestHeader(FINGERPRINT_HEADER, value)
    } catch (error) {
      // Silent: a broken hook must not break the page's ajax.
    }
  })

  // Set only once the registration has actually returned. Setting it first
  // means a throwing `ajaxPrefilter` leaves the instance marked as installed
  // with no prefilter on it, and the `window.load` retry then returns early on
  // the flag and never registers one.
  jq[INSTALLED_FLAG] = true

  return true
}

/**
 * Register on the sprockets jQuery, and on the pack-local one when different.
 *
 * @param {Object} [packJquery] - The ProvidePlugin-injected pack instance.
 * @returns {boolean} True when the *sprockets* instance was reached — the only
 *   one that matters for the 58 legacy files, and so the only one the retry
 *   below keys off.
 */
export const attachJqueryPrefilters = (packJquery) => {
  const sprocketsJquery =
    typeof window !== 'undefined' ? window.jQuery : undefined
  const attachedToSprockets = registerJqueryPrefilter(sprocketsJquery)

  if (packJquery && packJquery !== sprocketsJquery) {
    registerJqueryPrefilter(packJquery)
  }

  return attachedToSprockets
}

/**
 * Fill the hidden `user[fingerprint]` field, when the page has one.
 *
 * A page with no such field (every page that is not one of the three login
 * views) is a silent no-op. An empty field is the documented outcome on a first
 * visit — the form submits immediately and the event records NULL.
 *
 * @param {Document|Element} [root] - Search root; defaults to `document`.
 * @returns {boolean} True when a field was populated.
 */
export const populateLoginFingerprintField = (root) => {
  try {
    const scope = root || (typeof document !== 'undefined' ? document : null)
    if (!scope || typeof scope.querySelectorAll !== 'function') return false

    const value = fingerprintHeadersFor('/')[FINGERPRINT_HEADER]
    if (!value) return false

    const fields = scope.querySelectorAll(LOGIN_FIELD_SELECTOR)
    if (!fields.length) return false

    fields.forEach((field) => {
      field.value = value
    })

    return true
  } catch (error) {
    // Leaving the field empty is the designed fallback, not an error path.
    return false
  }
}

let submitPopulationInstalled = false

/**
 * Repopulate the hidden field as the login form is submitted.
 *
 * DOM-ready population alone loses a race that matters: on a cold browser
 * `primeFingerprint()` has to load the agent and run `fp.get()` (~100-500 ms),
 * and a login submitted inside that window posts an empty `user[fingerprint]`
 * and records NULL — the very population this work exists to fix, and exactly
 * the shape autofilled credentials plus Enter produce.
 *
 * This reads `getPrimedFingerprint()` **synchronously** and never awaits, so it
 * does not delay a submit (Key Decision 5) and a genuinely cold first visit
 * still posts an empty field (Scenario #6). It only closes the window between
 * DOM-ready and submit, which is most of it.
 *
 * Registered in the capture phase so it runs before any handler that might stop
 * propagation, and on `document` so the three login views need no per-view wiring.
 */
const attachLoginSubmitPopulation = () => {
  if (submitPopulationInstalled) return
  if (typeof document === 'undefined' || !document.addEventListener) return

  document.addEventListener(
    'submit',
    (event) => {
      try {
        const form = event && event.target
        if (!form || typeof form.querySelectorAll !== 'function') return
        populateLoginFingerprintField(form)
      } catch (error) {
        // Silent: telemetry must never break a login submit.
      }
    },
    true
  )

  submitPopulationInstalled = true
}

/** Reset the submit-listener guard. Test-only. */
export const resetSubmitPopulationGuard = () => {
  submitPopulationInstalled = false
}

const onDomReady = (callback) => {
  if (typeof document === 'undefined') return

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true })
  } else {
    callback()
  }
}

/**
 * Prime the visitor ID and wire up every global attach point.
 *
 * Nothing here awaits before returning: the axios interceptor is registered
 * synchronously, and the rest is scheduled. Priming is kicked off immediately so
 * the value is as likely as possible to be there by the time the page issues its
 * first request.
 *
 * @param {Object} [options]
 * @param {Object} [options.packJquery] - The ProvidePlugin-injected `$`.
 * @returns {Promise<void>} Resolves once priming settles. Callers may ignore it;
 *   it exists so a test can await the priming step.
 */
export const installFingerprintAttachments = ({ packJquery } = {}) => {
  installAxiosDefaultInterceptor()

  // Registered before DOM-ready on purpose: `document.addEventListener` works
  // straight away, and this is the hook that catches a submit landing while
  // priming is still in flight.
  attachLoginSubmitPopulation()

  const primed = primeFingerprint()
    .then(() => {
      // The field may not exist yet when priming wins the race against the DOM,
      // so this runs on both edges. Population is idempotent.
      onDomReady(() => populateLoginFingerprintField())
    })
    .catch(() => {})

  onDomReady(() => {
    populateLoginFingerprintField()

    if (attachJqueryPrefilters(packJquery)) return

    // The sprockets bundle is loaded `async` in production, so window.jQuery can
    // still be absent here. One retry, then give up without a sound.
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener(
        'load',
        () => {
          attachJqueryPrefilters(packJquery)
        },
        { once: true }
      )
    }
  })

  return primed
}

export default installFingerprintAttachments
